import { Add } from '@mui/icons-material'
import { Button, Divider, Stack, Typography, useMediaQuery, useTheme } from '@mui/material'
import dayjs, { Dayjs } from 'dayjs'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { getIsManagementOrAdministrator } from '../../store/redux/reducers/currentUserSlice'
import { RootState } from '../../store/redux/store'
import { EmploymentFormValues } from '../../store/types/form/EmploymentFormValues'
import { FormVacationPeriod } from '../../store/types/form/FormVacationPeriod'
import EmploymentFormVacationPeriodsDesktopView from './EmploymentFormVacationPeriodsDesktopView'
import EmploymentFormVacationPeriodsMobileView from './EmploymentFormVacationPeriodsMobileView'

type Props = {
  contractStart: Dayjs | null
  contractEnd: Dayjs | null
}

const EmploymentFormVacationPeriodsSection = ({ contractStart, contractEnd }: Props) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'))
  const isManagementOrAdministrator = useSelector((state: RootState) => getIsManagementOrAdministrator(state))

  const { control, formState, trigger, getValues } = useFormContext<EmploymentFormValues>()
  const {
    fields: vacationFields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: 'vacationPeriods',
  })

  const handleRemove = (index?: number | number[]) => {
    remove(index)
    trigger()
  }

  const addNewVacationPeriod = () => {
    const latestPeriod = getValues('vacationPeriods').at(-1)
    const latestEndDate = latestPeriod?.endDate

    const getStartDate = () => {
      return latestEndDate ? latestEndDate.add(1, 'day') : dayjs(contractStart)
    }

    const getEndDate = () => {
      if (latestEndDate) {
        return latestEndDate.add(2, 'day')
      }
      const isSameYear = contractEnd?.year() === contractStart?.year()
      return isSameYear ? dayjs(contractEnd) : dayjs(contractStart).endOf('year')
    }

    const newRow: FormVacationPeriod = {
      id: undefined,
      startDate: getStartDate(),
      endDate: getEndDate(),
      numberOfDays: 1,
      overtime: 0,
    }

    append(newRow)
    trigger()
  }

  const renderVacationPeriodErrors = (index: number) => {
    const periodErrors = formState.errors.vacationPeriods?.[index]

    if (!periodErrors) return null

    const errorMessagesSet = new Set<string>()

    Object.keys(periodErrors).forEach(fieldName => {
      const fieldError = periodErrors[fieldName as keyof typeof periodErrors]

      if (fieldError && typeof fieldError === 'object' && 'message' in fieldError) {
        const message = fieldError.message || ''
        if (message && typeof message === 'string') {
          errorMessagesSet.add(message)
        }
      }
    })

    return Array.from(errorMessagesSet).map((message, idx) => (
      <Typography key={idx} variant="body2" color="error" sx={{ mt: 1 }}>
        {message}
      </Typography>
    ))
  }

  const renderAddVacationPeriodButton = () => {
    if (!isManagementOrAdministrator) return null

    return (
      <Button variant="contained" color="secondary" size="small" startIcon={<Add />} onClick={addNewVacationPeriod}>
        {t('page.employments.table.addVacationPeriod')}
      </Button>
    )
  }

  return (
    <Stack gap={2}>
      <Stack direction={'row'} sx={{ justifyContent: 'space-between', alignItems: 'center', gap: 2 }} flexWrap={'wrap'}>
        <Typography component={'h4'} variant={'h5'} color="text.highEmphasis">
          {t('page.employments.table.vacationPeriods')}
        </Typography>
        {!isDownSm && renderAddVacationPeriodButton()}
      </Stack>

      {isDownSm ? (
        <>
          <EmploymentFormVacationPeriodsMobileView
            vacationFields={vacationFields}
            remove={handleRemove}
            contractStart={contractStart}
            contractEnd={contractEnd}
            renderVacationPeriodErrors={renderVacationPeriodErrors}
          />
          {renderAddVacationPeriodButton()}
          <Divider />
        </>
      ) : (
        <EmploymentFormVacationPeriodsDesktopView
          vacationFields={vacationFields}
          remove={handleRemove}
          contractStart={contractStart}
          contractEnd={contractEnd}
          renderVacationPeriodErrors={renderVacationPeriodErrors}
        />
      )}
    </Stack>
  )
}

export default EmploymentFormVacationPeriodsSection
